import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Input, Label } from 'reactstrap';
import ReactDatePicker from 'react-datepicker';
import { format, isValid, parse } from 'date-fns';

import translations from 'services/translations/translations';
import useDateLocale from 'services/translations/useDateLocale';

import 'style/layout/form/Datepicker.css';
import 'style/layout/form/TextWithDateInputField.css';

// The button opening the calendar; react-datepicker gives it its click handler
// eslint-disable-next-line react/display-name
const CalendarButton = React.forwardRef<
  HTMLButtonElement,
  { disabled?: boolean; label: string; onClick?: () => void }
>(({ disabled, label, onClick }, ref) => (
  <button
    aria-label={label}
    className="text-with-date-button"
    disabled={disabled}
    onClick={onClick}
    ref={ref}
    title={label}
    type="button"
  >
    <svg aria-hidden="true" height="20" viewBox="0 0 24 24" width="20">
      <path
        d="M7 2v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2h-2v2H9V2H7zm-2 8h14v10H5V10zm2 2v2h2v-2H7zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2zm-8 4v2h2v-2H7zm4 0v2h2v-2h-2z"
        fill="currentColor"
      />
    </svg>
  </button>
));

/**
 * A text field into which a date can also be chosen from a calendar, e.g. the validity of
 * an offer: either a date (written into the field) or free text such as "30 pv".
 */
const TextWithDateInputField = ({
  disabled,
  id,
  label,
  name,
  onChange,
  required,
  value,
}: {
  disabled?: boolean;
  id?: string;
  label?: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (event: any) => void;
  required?: boolean;
  value: string;
}): JSX.Element => {
  const dateLocale = useDateLocale();
  const { t } = useTranslation([translations.common]);

  // A date already in the field is shown chosen in the calendar
  const typedDate = parse(value.trim(), dateLocale.dateFormat, new Date());
  const selected = value.trim() !== '' && isValid(typedDate) ? typedDate : null;

  return (
    <FormGroup>
      {label === undefined ? null : (
        <Label className="label" for={id}>
          {label}
        </Label>
      )}
      <div className="text-with-date">
        <Input
          className="text-field"
          disabled={disabled}
          id={id}
          name={name}
          onChange={onChange}
          required={required === undefined ? false : required}
          type="text"
          value={value}
        />
        <ReactDatePicker
          calendarClassName="with-week-numbers"
          customInput={
            <CalendarButton
              disabled={disabled}
              label={t(`${translations.common}:Choose date`)}
            />
          }
          disabled={disabled}
          locale={dateLocale.datePicker}
          onChange={(date) => {
            if (date instanceof Date) {
              onChange({
                target: { name, value: format(date, dateLocale.dateFormat) },
              });
            }
          }}
          popperPlacement="bottom-end"
          selected={selected}
          showWeekNumbers
          weekLabel={t(`${translations.common}:Week short`)}
        />
      </div>
    </FormGroup>
  );
};

export default TextWithDateInputField;
